import {
  AbsenceStatusEnum,
  PortalAbsenceFragment,
  usePortalAbsenceAttachmentUploadMutation,
  usePortalAbsenceCancelMutation,
  usePortalAbsenceDecideMutation,
} from "@dashboard/graphql";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import { useRef } from "react";
import { MessageDescriptor, useIntl } from "react-intl";

import { ErrorText } from "./components";
import { portalLabels as l } from "./labels";
import { portalMessages as m } from "./messages";
import { formatDay, today } from "./utils";

export const ABSENCE_KIND_MESSAGES: Record<string, MessageDescriptor> = {
  VACATION: l.kindVacation,
  SICK_LEAVE: l.kindSickLeave,
  JUSTIFIED: l.kindJustified,
  UNJUSTIFIED: l.kindUnjustified,
  PARENTAL: l.kindParental,
  BREASTFEEDING: m.kindBreastfeeding,
  PRENATAL: m.kindPrenatal,
  OTHER: l.kindOther,
};

const STATUS_MESSAGES: Record<string, MessageDescriptor> = {
  PENDING: l.statusPending,
  APPROVED: l.statusApproved,
  REJECTED: l.statusRejected,
  CANCELLED: l.statusCancelled,
};

export const useAbsenceKindLabel = () => {
  const intl = useIntl();

  return (kind: string) =>
    ABSENCE_KIND_MESSAGES[kind] ? intl.formatMessage(ABSENCE_KIND_MESSAGES[kind]) : kind;
};

const STATUS_COLOR: Record<string, "default2" | "success1" | "critical1"> = {
  PENDING: "default2",
  APPROVED: "success1",
  REJECTED: "critical1",
  CANCELLED: "default2",
};

const AbsenceRow = ({
  absence,
  manager,
  showName,
  onChanged,
}: {
  absence: PortalAbsenceFragment;
  manager?: boolean;
  showName?: boolean;
  onChanged: () => void;
}) => {
  const intl = useIntl();
  const absenceKindLabel = useAbsenceKindLabel();
  const fileInput = useRef<HTMLInputElement>(null);
  const [upload, uploadOpts] = usePortalAbsenceAttachmentUploadMutation({
    onCompleted: () => onChanged(),
  });
  const [cancel, cancelOpts] = usePortalAbsenceCancelMutation({ onCompleted: () => onChanged() });
  const [decide, decideOpts] = usePortalAbsenceDecideMutation({ onCompleted: () => onChanged() });
  const active =
    absence.status === AbsenceStatusEnum.PENDING || absence.status === AbsenceStatusEnum.APPROVED;
  const canCancel =
    active &&
    (manager || absence.status === AbsenceStatusEnum.PENDING || absence.startDate > today());

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1}
      paddingY={2}
      borderBottomWidth={1}
      borderBottomStyle="solid"
      borderColor="default1"
    >
      <Box display="flex" gap={2} alignItems="baseline" flexWrap="wrap">
        {showName && (
          <Text size={3} fontWeight="bold">
            {absence.employeeName}
          </Text>
        )}
        <Text size={3} fontWeight={showName ? undefined : "bold"}>
          {absenceKindLabel(absence.kind)}
        </Text>
        <Text size={2}>
          {formatDay(absence.startDate)} – {formatDay(absence.endDate)} ·{" "}
          {absence.dailyPeriods.length > 0
            ? absence.dailyPeriods.map(period => `${period.start}–${period.end}`).join(", ")
            : intl.formatMessage(m.workingDays, { count: absence.workingDays })}
        </Text>
        <Text size={2} color={STATUS_COLOR[absence.status]}>
          {intl.formatMessage(STATUS_MESSAGES[absence.status])}
          {absence.decidedByName && ` · ${absence.decidedByName}`}
          {absence.decisionNote && ` — ${absence.decisionNote}`}
        </Text>
        <Box __flex={1} />
        {manager && absence.status === AbsenceStatusEnum.PENDING && (
          <>
            <Button
              size="small"
              variant="primary"
              disabled={decideOpts.loading}
              onClick={() => decide({ variables: { id: absence.id, approve: true } })}
            >
              {intl.formatMessage(m.accept)}
            </Button>
            <Button
              size="small"
              variant="secondary"
              disabled={decideOpts.loading}
              onClick={() => decide({ variables: { id: absence.id, approve: false } })}
            >
              {intl.formatMessage(m.refuse)}
            </Button>
          </>
        )}
        {active && (
          <>
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf,image/*"
              style={{ display: "none" }}
              onChange={event => {
                const file = event.target.files?.[0];

                if (file) {
                  upload({ variables: { absenceId: absence.id, file } });
                }

                event.target.value = "";
              }}
            />
            <Button
              size="small"
              variant="tertiary"
              disabled={uploadOpts.loading}
              onClick={() => fileInput.current?.click()}
            >
              {intl.formatMessage(m.attachProof)}
            </Button>
          </>
        )}
        {canCancel && (
          <Button
            size="small"
            variant="tertiary"
            disabled={cancelOpts.loading}
            onClick={() => cancel({ variables: { id: absence.id } })}
          >
            {intl.formatMessage(m.cancel)}
          </Button>
        )}
      </Box>
      {absence.reason && (
        <Text size={2} color="default2">
          {absence.reason}
        </Text>
      )}
      {absence.attachments.map(attachment => (
        <Text key={attachment.id} size={2}>
          📎{" "}
          <a href={attachment.downloadUrl} target="_blank" rel="noreferrer noopener">
            {attachment.originalName}
          </a>{" "}
          ({Math.ceil(attachment.size / 1024)} KB)
        </Text>
      ))}
      <ErrorText
        errors={[
          ...(uploadOpts.data?.absenceAttachmentUpload?.errors ?? []),
          ...(cancelOpts.data?.absenceCancel?.errors ?? []),
          ...(decideOpts.data?.absenceDecide?.errors ?? []),
        ]}
      />
    </Box>
  );
};

export const AbsenceList = ({
  absences,
  manager,
  showName,
  onChanged,
}: {
  absences: PortalAbsenceFragment[];
  manager?: boolean;
  showName?: boolean;
  onChanged: () => void;
}) => (
  <>
    {absences.map(absence => (
      <AbsenceRow
        key={absence.id}
        absence={absence}
        manager={manager}
        showName={showName}
        onChanged={onChanged}
      />
    ))}
  </>
);
